import { FC, useState } from 'react';
import { Card, Collapse, Divider, Empty, Table, TableColumnType } from 'antd';
import { DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useModals } from '@/components/modals/ModalProvider';
import {
	DMSFile,
	dmsGetDownloadUrl,
	dmsGetSignedUrl,
	DMSSection,
	DMSSubCategory,
} from '@/libs/fleet-api/services/dms';
import dayjs from 'dayjs';
import { addWatermark } from '@/utils/file-transform';
import { useUserData } from '@/libs/fleet-api/hooks';

const DMSDocumentDetail: FC<{
	keyword: string;
	data: DMSSubCategory;
	fetchType: string;
	categoryId: string;
	onDownloadFinish: () => void;
	onSpfSelected?: (spfNo: string, spfStatus: string) => void;
	onMppSelected?: (mppNo: string, agreementNo: string | null) => void;
	onDisbursementSelected?: (disbursementNo: string) => void;
}> = ({
	data,
	keyword,
	fetchType,
	categoryId,
	onDownloadFinish,
	onSpfSelected,
	onMppSelected,
	onDisbursementSelected,
}) => {
	const [activeKey, setActiveKey] = useState<string[]>([]);
	const [selectedSpfSectionId, setSelectedSpfSectionId] = useState<
		string | null
	>(null);
	const [selectedMppSectionId, setSelectedMppSectionId] = useState<
		string | null
	>(null);
	const [selectedDisbursementSectionId, setSelectedDisbursementSectionId] =
		useState<string | null>(null);
	const userData = useUserData();
	const { openPreviewModal } = useModals();
	const [isLoading, setIsLoading] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState<string | null>(null);

	// fetchType is available for conditional UI rendering based on fetch type
	// Currently not used but available for future implementation
	void fetchType;

	// Extract SPF number and status from section label (format: "spfNo|spfStatus")
	const extractSpfInfo = (label: string) => {
		const parts = label.split('|');
		return {
			spfNo: parts[0] || label,
			spfStatus: parts[1] || '',
		};
	};

	// Extract MPP number and agreement number from section label (format: "mppNo|agreementNo")
	const extractMppInfo = (label: string) => {
		const parts = label.split('|');
		return {
			mppNo: parts[0] || label,
			agreementNo: parts[1] && parts[1] !== 'null' ? parts[1] : null,
		};
	};

	const renderSection = ({ documents, id, label }: DMSSection) => {
		if (!documents || !Array.isArray(documents)) {
			return <Empty description="No documents available in this section" />;
		}

		const dataSource = documents.reduce(
			(arr, record, index) => {
				const no = index + 1;
				const name = record.name;
				const type = record.documentType;
				const description = record.description;

				if (record.files && record.files.length) {
					arr.push(
						...record.files.map((file, itemIndex) => {
							const rowSpan =
								!itemIndex && record.files.length >= 1
									? record.files.length
									: 0;

							return {
								key: `${index}-${itemIndex + 1}`,
								no,
								name,
								type,
								rowSpan,
								file,
								documentType: record.documentType,
								description,
							};
						}),
					);
				} else {
					arr.push({
						key: index.toString(),
						no,
						name,
						type,
						rowSpan: 1,
						file: undefined,
						documentType: record.documentType,
						description,
					});
				}
				return arr;
			},
			[] as {
				key: string;
				no: number;
				name: string;
				type: string;
				rowSpan: number;
				file?: DMSFile;
				documentType?: string;
				description?: string;
			}[],
		);

		const getTitleNameById = (id: string) => {
			switch (id) {
				case 'shareholder':
					return 'Nama Pemegang Saham';
				case 'commissioner-identity':
					return 'Nama Komisaris';
				case 'director-identity':
					return 'Nama Direktur';
				default:
					return 'Nama';
			}
		};

		const results = keyword
			? dataSource.filter((d) => {
					if (
						id === 'shareholder' ||
						id === 'commissioner-identity' ||
						id === 'director-identity'
					) {
						return (
							d.file?.documentNo
								?.toLowerCase()
								?.includes(keyword.toLowerCase()) ||
							d.name?.toLowerCase()?.includes(keyword.toLowerCase())
						);
					}

					return d.name?.toLowerCase().includes(keyword.toLowerCase());
				})
			: dataSource;

		if (!results.length) {
			return (
				<Empty
					description={
						keyword ? (
							<>
								Your search <b>{`"${keyword}"`}</b> did not match any documents
								in <b>{label}</b>.
							</>
						) : (
							'No Data'
						)
					}
				/>
			);
		}

		type DataSourceType = (typeof dataSource)[number];
		const columns: TableColumnType<DataSourceType>[] = [
			{
				title: 'No',
				align: 'center',
				render: (_, { no }) => no,
				onCell: (record) => {
					return {
						rowSpan: record.rowSpan,
						style: {
							verticalAlign: 'top',
							...(!record.rowSpan && {
								display: 'none',
							}),
						},
					};
				},
			},
			...(id === 'shareholder' ||
			id === 'commissioner-identity' ||
			id === 'director-identity'
				? [
						{
							title: 'NIK',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.description || record.file?.documentNo,
							onCell: (record: { rowSpan: number }) => {
								return {
									rowSpan: record.rowSpan,
									style: {
										verticalAlign: 'top',
										...(!record.rowSpan && {
											display: 'none',
										}),
									},
								};
							},
						},
						{
							title: getTitleNameById(id),
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record?.name,
							onCell: (record: { rowSpan: number }) => {
								return {
									rowSpan: record.rowSpan,
									style: {
										verticalAlign: 'top',
										...(!record.rowSpan && {
											display: 'none',
										}),
									},
								};
							},
						},
					]
				: [
						{
							title: 'Nama File',
							width: 300,
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.name,
							onCell: (record: { rowSpan: number }) => {
								return {
									rowSpan: record.rowSpan,
									style: {
										verticalAlign: 'top',
										whiteSpace: 'normal' as const,
										wordBreak: 'break-word' as const,
										...(!record.rowSpan && {
											display: 'none',
										}),
									},
								};
							},
						},
					]),
			{
				title: 'View File',
				render: (_: unknown, record: (typeof dataSource)[number]) => {
					if (!record.file?.fileId) return null;

					const uniqueLoadingId = `${id}-${record.documentType}-${record.file.fileId}`;
					if (isLoading === uniqueLoadingId)
						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
								}}
							>
								<LoadingOutlined
									spin
									style={{ color: '#1557BC' }}
								/>
							</div>
						);

					return (
						<a
							href="#"
							onClick={async (e) => {
								e.preventDefault();
								if (isLoading === uniqueLoadingId) return;

								setIsLoading(uniqueLoadingId);

								try {
									const {
										data: { signedUrl },
									} = await dmsGetSignedUrl(record.file!.fileId);

									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
										return undefined;
									};

									const { fileId, fileName } = record.file ?? {};
									const fileType = getDocumentType(record.file!.fileName);
									// if file type is not supported, open the original file
									if (!fileType) {
										openPreviewModal({
											id: String(fileId),
											name: fileName!,
											format: getDocumentType(fileName!),
											url: signedUrl,
										});
										return;
									}

									// Add watermark to the file
									const watermarkedBlob = await addWatermark(
										signedUrl,
										fileType!,
										{
											text: `${userData?.fullName}\n${dayjs().format('DD/MM/YYYY - HH:mm:ss')}`,
											opacity: 0.2,
											fontSize:
												getDocumentType(fileName!) === 'pdf' ? 280 : 160,
											color: '#111111',
										},
									);
									const watermarkedUrl = URL.createObjectURL(watermarkedBlob);

									openPreviewModal({
										id: String(fileId),
										name: fileName!,
										format: getDocumentType(fileName!),
										url: watermarkedUrl,
									});
									// Clean up the object URL when the modal is closed
									setTimeout(() => {
										URL.revokeObjectURL(watermarkedUrl);
									}, 1000);
								} finally {
									setIsLoading(null);
								}
							}}
						>
							{record.file?.fileName ?? '-'}
						</a>
					);
				},
			},
			...(id === 'section-company-legality' ||
			id === 'section-business-legality'
				? [
						{
							title: 'No Dokumen',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.file?.documentNo,
						},
						{
							title: 'Tanggal Upload',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.file?.uploadDate &&
								dayjs(record.file.uploadDate).format('DD MMM YYYY'),
						},
					]
				: [
						{
							title: 'Tanggal Upload',
							render: (_: unknown, record: (typeof dataSource)[number]) =>
								record.file?.uploadDate &&
								dayjs(record.file.uploadDate).format('DD MMM YYYY'),
						},
					]),
			{
				title: 'Download',
				align: 'center',
				fixed: 'right',
				render: (_: unknown, record: (typeof dataSource)[number]) => {
					const uniqueDownloadId = `${id}-${record.documentType}-${record.file?.fileId}`;
					if (isDownloading === uniqueDownloadId) {
						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '100%',
								}}
							>
								<LoadingOutlined
									spin
									style={{ color: '#1557BC' }}
								/>
							</div>
						);
					}
					if (record.file?.fileId) {
						return (
							<a
								href="#"
								onClick={async (e) => {
									e.preventDefault();
									if (isDownloading === uniqueDownloadId) return;

									setIsDownloading(uniqueDownloadId);
									try {
										const {
											data: { signedUrl },
										} = await dmsGetDownloadUrl(record.file!.fileId);

										const getDocumentType = (name: string) => {
											if (name.endsWith('pdf')) return 'pdf';
											if (name.endsWith('jpeg')) return 'jpeg';
											if (name.endsWith('jpg')) return 'jpg';
											if (name.endsWith('png')) return 'png';
											return undefined;
										};

										const fileType = getDocumentType(record.file!.fileName);
										if (!fileType) {
											// If file type is not supported, download original
											window.open(signedUrl, '_blank');
											return;
										}

										// Add watermark to the file
										const watermarkedBlob = await addWatermark(
											signedUrl,
											fileType,
											{
												text: `${userData?.fullName}\n${dayjs().format('DD/MM/YYYY - HH:mm:ss')}`,
												opacity: 0.2,
												fontSize:
													getDocumentType(record.file!.fileName) === 'pdf'
														? 280
														: 160,
												color: '#111111',
											},
										);
										const watermarkedUrl = URL.createObjectURL(watermarkedBlob);

										// Create a temporary link to download the watermarked file
										const link = document.createElement('a');
										link.href = watermarkedUrl;
										link.download = record.file!.fileName;
										document.body.appendChild(link);
										link.click();
										document.body.removeChild(link);

										// Clean up the object URL
										setTimeout(() => {
											URL.revokeObjectURL(watermarkedUrl);
										}, 1000);

										onDownloadFinish();
									} finally {
										setIsDownloading(null);
									}
								}}
							>
								<DownloadOutlined />
							</a>
						);
					}

					return (
						<a
							href="#"
							onClick={(e) => e.preventDefault()}
							style={{ cursor: 'default' }}
						>
							<DownloadOutlined style={{ color: '#ccc' }} />
						</a>
					);
				},
			},
			{
				title: 'Last Download',
				align: 'center',
				render: (_: unknown, record: (typeof dataSource)[number]) =>
					record.file?.lastDownloadBy,
			},
			{
				title: 'Jumlah Download',
				align: 'center',
				render: (_: unknown, record: (typeof dataSource)[number]) =>
					record.file?.downloadCount,
			},
		];

		return (
			<Table
				bordered
				pagination={false}
				scroll={{ x: 'max-content' }}
				dataSource={results}
				columns={columns}
			/>
		);
	};

	if (!data.sections || !Array.isArray(data.sections)) {
		return <Empty description="No sections available" />;
	}

	// Special handling for covenant-document: show SPF selection first
	const isCovenantDocument = categoryId.toLowerCase() === 'covenant-document';
	const allSectionsAreSpf =
		isCovenantDocument &&
		data.sections.length > 0 &&
		data.sections.every((section) => section.id.startsWith('spf-'));

	// Special handling for financing-disbursement-document: show MPP selection first
	const isFinancingDisbursementDocument =
		categoryId.toLowerCase() === 'financing-disbursement-document';
	const mppSelectionSections = isFinancingDisbursementDocument
		? data.sections.filter((section) => section.id.startsWith('mpp-select-'))
		: [];

	// If covenant-document with multiple SPF sections and no SPF selected yet, show SPF selection
	if (allSectionsAreSpf && !selectedSpfSectionId) {
		const filteredSpfSections = keyword
			? data.sections.filter((section) =>
					section.label.toLowerCase().includes(keyword.toLowerCase()),
				)
			: data.sections;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				<h3
					style={{
						marginBottom: 0,
						fontSize: 16,
						fontWeight: 600,
						backgroundColor: '#E3F2FD',
						padding: '12px 16px',
						borderRadius: '4px 4px 0 0',
					}}
				>
					Pilih Surat Persetujuan Fasilitas (SPF)
				</h3>
				{filteredSpfSections.length ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 12,
						}}
					>
						{filteredSpfSections.map((section) => {
							const { spfNo } = extractSpfInfo(section.label);
							return (
								<Card
									key={section.id}
									style={{
										cursor: 'pointer',
										border: '1px solid #E0E0E0',
										borderRadius: 4,
									}}
									onClick={() => {
										setSelectedSpfSectionId(section.id);
										const { spfNo: selectedSpfNo, spfStatus } = extractSpfInfo(
											section.label,
										);
										if (onSpfSelected) {
											onSpfSelected(selectedSpfNo, spfStatus);
										}
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 12,
										}}
									>
										<svg
											width="32"
											height="32"
											viewBox="0 0 32 32"
											fill="none"
										>
											<path
												d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
												fill="#FFA000"
											/>
											<path
												d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
												fill="#FFCA28"
											/>
										</svg>
										<strong>{spfNo}</strong>
									</div>
								</Card>
							);
						})}
					</div>
				) : (
					<div
						style={{
							backgroundColor: '#fff',
							padding: '16px',
							borderRadius: '0 0 4px 4px',
							border: '1px solid #E0E0E0',
							borderTop: 'none',
						}}
					>
						<Empty
							description={
								keyword ? (
									<>
										Your search <b>{`"${keyword}"`}</b> did not match any SPF.
									</>
								) : (
									'No SPF available'
								)
							}
						/>
					</div>
				)}
			</div>
		);
	}

	// If financing-disbursement-document with MPP selection sections and no MPP selected yet, show MPP selection
	if (
		isFinancingDisbursementDocument &&
		mppSelectionSections.length > 0 &&
		!selectedMppSectionId
	) {
		const filteredMppSections = keyword
			? mppSelectionSections.filter((section) =>
					section.label.toLowerCase().includes(keyword.toLowerCase()),
				)
			: mppSelectionSections;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
				<h3
					style={{
						marginBottom: 0,
						fontSize: 16,
						fontWeight: 600,
						backgroundColor: '#E3F2FD',
						padding: '12px 16px',
						borderRadius: '4px 4px 0 0',
					}}
				>
					Pilih Memo Pencairan Pembiayaan (MPP)
				</h3>
				{filteredMppSections.length ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 12,
						}}
					>
						{filteredMppSections.map((section) => {
							const { mppNo } = extractMppInfo(section.label);
							return (
								<Card
									key={section.id}
									style={{
										cursor: 'pointer',
										border: '1px solid #E0E0E0',
										borderRadius: 4,
									}}
									onClick={() => {
										setSelectedMppSectionId(section.id);
										setSelectedDisbursementSectionId(null);
										const { mppNo: selectedMppNo, agreementNo } =
											extractMppInfo(section.label);
										if (onMppSelected) {
											onMppSelected(selectedMppNo, agreementNo);
										}
										// Reset disbursement selection when selecting new MPP
										if (onDisbursementSelected) {
											onDisbursementSelected('');
										}
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 12,
										}}
									>
										<svg
											width="32"
											height="32"
											viewBox="0 0 32 32"
											fill="none"
										>
											<path
												d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
												fill="#FFA000"
											/>
											<path
												d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
												fill="#FFCA28"
											/>
										</svg>
										<strong>{mppNo}</strong>
									</div>
								</Card>
							);
						})}
					</div>
				) : (
					<div
						style={{
							backgroundColor: '#fff',
							padding: '16px',
							borderRadius: '0 0 4px 4px',
							border: '1px solid #E0E0E0',
							borderTop: 'none',
						}}
					>
						<Empty
							description={
								keyword ? (
									<>
										Your search <b>{`"${keyword}"`}</b> did not match any MPP.
									</>
								) : (
									'No MPP available'
								)
							}
						/>
					</div>
				)}
			</div>
		);
	}

	// Special handling for financing-disbursement-document after MPP is selected
	if (
		isFinancingDisbursementDocument &&
		selectedMppSectionId &&
		!selectedDisbursementSectionId
	) {
		// Extract MPP ID from selected section ID (format: "mpp-select-{mppId}")
		const mppId = selectedMppSectionId.replace('mpp-select-', '');

		// Get MPP documents section
		const mppDocsSection = data.sections.find(
			(section) => section.id === `mpp-docs-${mppId}`,
		);

		// Get disbursement selection sections
		const disbursementSelectionSections = data.sections.filter((section) =>
			section.id.startsWith(`disbursement-select-`),
		);

		// Filter disbursement selection sections that belong to this MPP
		// We need to check if the disbursement belongs to this MPP by checking if there are related sections
		const mppRelatedDisbursementIds = new Set<string>();
		data.sections.forEach((section) => {
			if (section.id.startsWith(`mpp-${mppId}-disbursement-`)) {
				const disbursementId = section.id.replace(
					`mpp-${mppId}-disbursement-`,
					'',
				);
				mppRelatedDisbursementIds.add(disbursementId);
			}
		});

		const filteredDisbursementSections = disbursementSelectionSections.filter(
			(section) => {
				const disbursementId = section.id.replace('disbursement-select-', '');
				return mppRelatedDisbursementIds.has(disbursementId);
			},
		);

		const filteredDisbursementSectionsByKeyword = keyword
			? filteredDisbursementSections.filter((section) =>
					section.label.toLowerCase().includes(keyword.toLowerCase()),
				)
			: filteredDisbursementSections;

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
				{/* MPP Documents Table */}
				{mppDocsSection &&
					mppDocsSection.documents &&
					Array.isArray(mppDocsSection.documents) &&
					mppDocsSection.documents.length > 0 && (
						<div>
							<h3
								style={{
									marginBottom: 16,
									fontSize: 16,
									fontWeight: 600,
									backgroundColor: '#E3F2FD',
									padding: '12px 16px',
									borderRadius: 4,
								}}
							>
								Dokumen Memo Pencairan Pembiayaan (MPP)
							</h3>
							{renderSection(mppDocsSection)}
						</div>
					)}

				{/* Disbursement Selection - Always show even if empty */}
				<div>
					<h3
						style={{
							marginBottom: 16,
							fontSize: 16,
							fontWeight: 600,
							backgroundColor: '#E3F2FD',
							padding: '12px 16px',
							borderRadius: 4,
						}}
					>
						Pilih No. Dokumen Kesepakatan Realisasi Pencairan
					</h3>
					{filteredDisbursementSectionsByKeyword.length ? (
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: 12,
							}}
						>
							{filteredDisbursementSectionsByKeyword.map((section) => {
								return (
									<Card
										key={section.id}
										style={{
											cursor: 'pointer',
											border: '1px solid #E0E0E0',
											borderRadius: 4,
										}}
										onClick={() => {
											setSelectedDisbursementSectionId(section.id);
											if (onDisbursementSelected) {
												onDisbursementSelected(section.label);
											}
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: 12,
											}}
										>
											<svg
												width="32"
												height="32"
												viewBox="0 0 32 32"
												fill="none"
											>
												<path
													d="M26.6641 7.99967H14.6641L11.9974 5.33301H5.33073C3.86406 5.33301 2.66406 6.53301 2.66406 7.99967V13.333H29.3307V10.6663C29.3307 9.19967 28.1307 7.99967 26.6641 7.99967Z"
													fill="#FFA000"
												/>
												<path
													d="M26.6641 8H5.33073C3.86406 8 2.66406 9.2 2.66406 10.6667V24C2.66406 25.4667 3.86406 26.6667 5.33073 26.6667H26.6641C28.1307 26.6667 29.3307 25.4667 29.3307 24V10.6667C29.3307 9.2 28.1307 8 26.6641 8Z"
													fill="#FFCA28"
												/>
											</svg>
											<strong>{section.label}</strong>
										</div>
									</Card>
								);
							})}
						</div>
					) : (
						<Empty
							description={
								keyword ? (
									<>
										Your search <b>{`"${keyword}"`}</b> did not match any
										disbursement documents.
									</>
								) : (
									'No disbursement documents available'
								)
							}
						/>
					)}
				</div>
			</div>
		);
	}

	// Filter sections based on selected SPF (for covenant-document) or selected MPP (for financing-disbursement-document) or all sections
	const sectionsToRender = (() => {
		if (isCovenantDocument && selectedSpfSectionId) {
			return data.sections.filter(
				(section) => section.id === selectedSpfSectionId,
			);
		}
		if (
			isFinancingDisbursementDocument &&
			selectedMppSectionId &&
			selectedDisbursementSectionId
		) {
			// Extract MPP ID and Disbursement ID
			const mppId = selectedMppSectionId.replace('mpp-select-', '');
			const disbursementId = selectedDisbursementSectionId.replace(
				'disbursement-select-',
				'',
			);
			// Filter sections that belong to the selected MPP and Disbursement
			// Include disbursement documents, PO documents, and Agreement documents
			return data.sections.filter(
				(section) =>
					section.id === `mpp-${mppId}-disbursement-${disbursementId}` ||
					section.id.startsWith(
						`mpp-${mppId}-disbursement-${disbursementId}-po-`,
					) ||
					section.id.startsWith(
						`mpp-${mppId}-disbursement-${disbursementId}-agreement-`,
					),
			);
		}
		return data.sections;
	})();

	const sections = sectionsToRender.filter(
		(section) =>
			section?.documents &&
			Array.isArray(section.documents) &&
			section.documents.length > 0,
	);

	if (sections.length === 0) {
		return (
			<Empty
				description={
					keyword ? (
						<>
							Your search <b>{`"${keyword}"`}</b> did not match any documents.
						</>
					) : (
						'No documents available'
					)
				}
			/>
		);
	}

	// For sections with id === 'default', render directly as table
	// For covenant-document with selected SPF, render directly as table (no accordion)
	// For financing-disbursement-document with selected MPP, render directly as table (no accordion)
	// For other sections (like SPF folders), render as accordion
	const hasDefaultSection =
		sections.length === 1 && sections[0].id === 'default';
	const isCovenantWithSelectedSpf =
		isCovenantDocument && selectedSpfSectionId && sections.length > 0;
	const isFinancingDisbursementWithSelectedDisbursement =
		isFinancingDisbursementDocument &&
		selectedMppSectionId &&
		selectedDisbursementSectionId &&
		sections.length > 0;

	const showFinancingApprovalTitle =
		categoryId === 'financing-approval-document';
	const showCovenantTitle = isCovenantDocument && selectedSpfSectionId;

	const renderFinancingDisbursementView = () => {
		const mppId = selectedMppSectionId!.replace('mpp-select-', '');
		const disbursementId = selectedDisbursementSectionId!.replace(
			'disbursement-select-',
			'',
		);
		const mainSection = sections.find(
			(s) => s.id === `mpp-${mppId}-disbursement-${disbursementId}`,
		);
		const poSections = sections.filter((s) => s.metadata?.type === 'po');

		const agreementsByPo = sections
			.filter((s) => s.metadata?.type === 'agreement')
			.reduce(
				(acc, agreement) => {
					const poId = agreement.metadata?.poId;
					if (poId) {
						if (!acc[poId]) acc[poId] = [];
						acc[poId].push(agreement);
					}
					return acc;
				},
				{} as Record<number, DMSSection[]>,
			);

		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
				{/* Skema Section */}
				{mainSection && (
					<Collapse
						defaultActiveKey={['skema']}
						items={[
							{
								key: 'skema',
								label: 'Skema',
								children: (
									<div>
										<div style={{ marginBottom: 16 }}>
											Skema Pembiayaan: {mainSection.metadata?.scheme || '-'}
										</div>
										<Divider />
										<h4 style={{ marginBottom: 16 }}>List Dokumen</h4>
										{renderSection(mainSection)}
									</div>
								),
							},
						]}
					/>
				)}

				{/* Purchase Order List Section */}
				{poSections.length > 0 && (
					<div>
						<h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
							List Purchase Order
						</h3>
						<Collapse
							defaultActiveKey={[]}
							items={poSections.map((poSection) => {
								const poId = poSection.metadata?.poId;
								const relatedAgreements = poId
									? agreementsByPo[poId] || []
									: [];

								return {
									key: poSection.id,
									label: poSection.label,
									children: (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: 16,
											}}
										>
											{renderSection(poSection)}
											{relatedAgreements.length > 0 && (
												<Collapse
													defaultActiveKey={[]}
													items={relatedAgreements.map((agreement) => {
														// Extract agreement number from label "Agreement {no}"
														const agreementNo = agreement.label.replace(
															'Agreement ',
															'',
														);
														return {
															key: agreement.id,
															label: `No. Agreement (${agreementNo})`,
															style: {
																marginBottom: 8,
																border: 'none',
																borderRadius: 4,
															},
															styles: {
																header: {
																	background: '#FFFDE7',
																	borderRadius: 4,
																},
															},
															children: (
																<div>
																	<h5
																		style={{
																			marginBottom: 8,
																			fontWeight: 600,
																			fontSize: 14,
																			color: '#666',
																		}}
																	>
																		List Dokumen Agreement
																	</h5>
																	{renderSection(agreement)}
																</div>
															),
														};
													})}
												/>
											)}
										</div>
									),
								};
							})}
						/>
					</div>
				)}
			</div>
		);
	};

	return (
		<>
			{showFinancingApprovalTitle && (
				<h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
					List Dokumen Persetujuan Pembiayaan
				</h3>
			)}
			{showCovenantTitle && (
				<h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 600 }}>
					List Dokumen Covenant
				</h3>
			)}
			{hasDefaultSection || isCovenantWithSelectedSpf ? (
				renderSection(sections[0])
			) : isFinancingDisbursementWithSelectedDisbursement ? (
				renderFinancingDisbursementView()
			) : (
				<Collapse
					defaultActiveKey={[]}
					activeKey={
						keyword ? sections.map((section) => section.id) : activeKey
					}
					onChange={(keys) => setActiveKey(keys as string[])}
					items={sections.map((section) => {
						// For covenant-document, extract SPF number from label (format: "spfNo|spfStatus")
						const displayLabel = isCovenantDocument
							? extractSpfInfo(section.label).spfNo
							: section.label;
						return {
							key: section.id,
							label: displayLabel,
							children: renderSection(section),
						};
					})}
				/>
			)}
		</>
	);
};

export default DMSDocumentDetail;
